import React, {useState} from 'react'

import {
  Alerts,
  DateTime,
  type DownloadPdfResult,
  LabeledInput,
  locationTourRunsSummaryDownload,
  NoResults,
  RPCDocs,
  SelectUser,
  useDownloadsApiServiceContext,
  useLocationCheckpointTourRuns,
  useLocationContext,
  usePaginationState,
  UserAnPCheck,
  UserName,
  useRPCContext,
  useUrlState,
} from '@therms/react-modules'
import {
  Button,
  ButtonWithActionModal,
  Card,
  ContextMenu,
  DateTimeRangePicker,
  Dropdown,
  Icon,
  Label,
  Modal,
  Pagination,
  Tag,
  toastManager,
} from '@therms/atalaya'
import {CheckpointTourRunDetails} from './components/CheckpointTourRunDetails'
import dayjs from 'dayjs'
import {downloadBase64AsPdf, humanizeMinutes} from '@therms/web-js'
import {useSetState, useUpdateEffect} from 'ahooks'
import {IoMdDownload} from 'react-icons/io'
import {MdDataset, MdDescription, MdRefresh} from 'react-icons/md'
import {IoClose} from 'react-icons/io5'

function CheckpointTourRunsList() {
  const {client} = useDownloadsApiServiceContext()
  const {location, locationId} = useLocationContext(true)
  const {rpcService} = useRPCContext()
  const [showTourRunIdDetailModal, setShowTourRunIdDetailModal] =
    useState<string>()
  const {recordsPerPage, setRecordsPerPage, setSkippedRecords, skippedRecords} =
    usePaginationState('locchkpttrrnlst', [])

  const [urlState, setUrlState] = useUrlState<{
    dateTimeRange: {start?: string; end?: string}
    userId: string | undefined
    showDateTimeFilter: boolean
    showUserIdFilter: boolean
  }>('checkpointtourfilters', {
    dateTimeRange: {end: undefined, start: undefined},
    showDateTimeFilter: false,
    showUserIdFilter: false,
    userId: undefined,
  })

  const [state, setState] = useSetState(urlState)

  useUpdateEffect(() => {
    setUrlState(state)
    setSkippedRecords(0)
  }, [state])

  const {data} = useLocationCheckpointTourRuns({
    fromDate: state?.dateTimeRange.start,
    locationId,
    pagination: {limit: recordsPerPage, skip: skippedRecords},
    toDate: state?.dateTimeRange.end,
    userId: state?.userId,
  })

  const handleCheckpointToursPDFDownload = () =>
    client.locations.DownloadLocationCheckpointTours(
      {
        body: {
          fromDate: state?.dateTimeRange.start,
          locationId,
          toDate: state?.dateTimeRange.end,
          userId: state?.userId,
        },
      },
      {timeout: 60_000},
    )
      .then((response) => {
        const result = response as DownloadPdfResult
        if (result.pdfBase64) {
          downloadBase64AsPdf(
            `${location?.name} Tour Runs - ${dayjs(
              state?.dateTimeRange.start,
            ).format()} - ${dayjs(state?.dateTimeRange.end).format()}`,
            result.pdfBase64,
          )
        }
      })
      .catch((err: any) => {
        console.error('err downloading entry', err)
      })

  const handleCheckpointTourRunsSummaryDownload = async (
    format: 'csv' | 'excel',
  ) => {
    const tourRunsResponse =
      await rpcService.Locations.GetLocationCheckpointTourRunsList.v2.call(
        {
          fromDate: state?.dateTimeRange.start,
          locationId,
          pagination: {limit: 5000},
          toDate: state?.dateTimeRange.end,
          userId: state?.userId,
        },
        {
          timeout: 60_000,
        },
      )

    const userIdsById = tourRunsResponse.data.checkpointTourRuns.reduce<
      Record<string, string>
    >((acc, tourRun) => {
      if (!acc[tourRun.startedByUserId]) {
        acc[tourRun.startedByUserId] = tourRun.startedByUserId
      }

      return acc
    }, {})

    const usersResponse = await rpcService.Users.GetUsersList.v1.call(
      {
        idFilter: Object.keys(userIdsById),
        pagination: {limit: 5000},
      },
      {
        timeout: 60_000,
      },
    )

    type User = RPCDocs.Users.GetUsersList.v1.User
    const usersById = usersResponse.data.users.reduce<Record<string, User>>(
      (acc, user) => {
        acc[user.id] = user
        return acc
      },
      {},
    )

    locationTourRunsSummaryDownload(
      tourRunsResponse.data.checkpointTourRuns,
      {usersById},
      {
        fileName: `${location.name} Checkpoint Tour Runs`,
        format,
      },
    )
  }

  const handleDownload = (format: 'pdf' | 'csv' | 'excel') => {
    const toastId = Alerts.info(
      <div>
        <Icon Icon={MdRefresh} spin /> Starting download, please wait...
      </div>,
      {autoClose: false, position: 'top-center'},
    )

    const request =
      format === 'pdf'
        ? handleCheckpointToursPDFDownload
        : () => handleCheckpointTourRunsSummaryDownload(format)

    request()
      .catch((err: any) => {
        console.error('err downloading tours', err)
      })
      .finally(() => toastManager.dismiss(toastId))
  }

  return (
    <div>
      <div className="gap-xs flex flex-wrap">
        <div className="gap-xxs grid grow grid-cols-2 items-center md:grid-cols-3 xl:grid-cols-4">
          <div className="col-span-2 min-w-fit">
            <DateTimeRangePicker
              onChangeValue={(val) => {
                setState({
                  dateTimeRange: val,
                })
              }}
              onClearValue={() =>
                setState({
                  dateTimeRange: {end: undefined, start: undefined},
                })
              }
              value={state.dateTimeRange}
            />
          </div>

          <div className="col-span-2 sm:col-span-1 lg:col-span-1">
            <LabeledInput
              label="User"
              nameElement={<UserName userId={state.userId} />}
              inputElement={
                <SelectUser
                  rolesFilter={['ops']}
                  isClearable
                  menuIsOpen
                  onChangeValue={(val) => {
                    setState({
                      userId: val,
                    })
                  }}
                  placeholder="User"
                  value={state.userId}
                />
              }
              value={state.userId}
            />
          </div>
        </div>

        <UserAnPCheck
          check={(user) => user.anp.role.ops.access.canDownloadAndExportData}
        >
          <div className="pr-xs flex grow items-center justify-end md:grow-0">
            <div className="w-full">
              <Dropdown
                className="ml-auto"
                controller={({close, isOpen, open}) => (
                  <Button
                    className="h-full"
                    onClick={isOpen ? close : open}
                    size="sm"
                    outline
                    variant="main"
                  >
                    <Icon Icon={IoMdDownload} size="sm" /> Download
                  </Button>
                )}
              >
                <ContextMenu.Option onClick={() => handleDownload('pdf')}>
                  <div className="min-w-[100px]">
                    <Icon Icon={MdDescription} /> <span>PDF</span>
                  </div>
                </ContextMenu.Option>

                <ContextMenu.Divider />

                <ContextMenu.Option onClick={() => handleDownload('csv')}>
                  <div className="min-w-[100px]">
                    <Icon Icon={MdDataset} /> <span>CSV</span>
                  </div>
                </ContextMenu.Option>

                <ContextMenu.Option onClick={() => handleDownload('excel')}>
                  <div className="min-w-[100px]">
                    <Icon Icon={MdDataset} /> <span>Excel</span>
                  </div>
                </ContextMenu.Option>
              </Dropdown>
            </div>
          </div>
        </UserAnPCheck>
      </div>

      {data?.checkpointTourRuns?.length ? (
        <div className="mt-base space-y-xs">
          {data?.checkpointTourRuns.map(
            ({
              checkpointScansCompleted,
              checkpointScansRemaining,
              checkpointScansTotal,
              checkpointTour,
              endDate,
              id,
              startDate,
              startedByUserId,
              status,
            }) => (
              <Card key={id}>
                <div className="inline-flex w-full justify-between">
                  <div>{checkpointTour.name} </div>

                  <div>
                    {status === 'inprogress' ? (
                      <div className="gap-x-sm inline-flex items-start">
                        {checkpointScansRemaining > 0 && (
                          <span className="text-color-neutral">
                            {checkpointScansRemaining} scans left
                          </span>
                        )}
                        <Tag value="In-Progress" variant="main" />{' '}
                        <ButtonWithActionModal
                          actionText={
                            <>
                              <Icon Icon={IoClose} /> End Tour
                            </>
                          }
                          body="Are you sure you want to end this Tour?"
                          onAction={() =>
                            rpcService.Locations.EndLocationCheckpointTourRun.v1.call(
                              {checkpointTourRunId: id},
                            )
                          }
                          size="sm"
                          subtle
                          title="End Tour"
                          variant="critical"
                        />
                      </div>
                    ) : (
                      <div className="gap-x-sm inline-flex items-start">
                        {checkpointScansRemaining > 0 && (
                          <span className="text-color-caution">
                            {checkpointScansRemaining} scans incomplete out of{' '}
                            {checkpointScansTotal}
                          </span>
                        )}
                        <Tag value="Ended" variant="neutral" />
                      </div>
                    )}
                  </div>
                </div>

                <progress
                  className="w-full"
                  max={checkpointScansTotal}
                  value={checkpointScansCompleted}
                />

                <div className="inline-flex w-full justify-between">
                  <div className="grow">
                    <div className="grid grid-cols-3">
                      <div className="flex flex-col">
                        <Label>Started By</Label>
                        <UserName userId={startedByUserId} />
                      </div>

                      <div className="flex flex-col">
                        <Label>Started</Label>
                        <DateTime timestamp={startDate} />
                      </div>

                      {status === 'ended' ? (
                        <div className="flex flex-col">
                          <Label>Ended</Label>
                          {endDate && (
                            <div>
                              <DateTime mode="time" timestamp={endDate} />{' '}
                              <span className="text-color-neutral font-light">
                                (
                                {humanizeMinutes(
                                  dayjs(startDate).diff(endDate, 'minutes'),
                                )}
                                )
                              </span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex flex-col justify-end">
                          <em className="text-color-neutral">in-progress</em>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <Button
                      onClick={() => setShowTourRunIdDetailModal(id)}
                      subtle
                      variant="main"
                    >
                      Details
                    </Button>
                  </div>
                </div>
              </Card>
            ),
          )}

          <Pagination
            className="mt-base"
            onPageClick={setSkippedRecords}
            onSetRecordsPerPage={setRecordsPerPage}
            recordsPerPage={recordsPerPage}
            showDescription
            skippedRecords={skippedRecords}
            totalRecords={data?.total}
          />
        </div>
      ) : (
        <NoResults text="No Checkpoint Tours" />
      )}

      {showTourRunIdDetailModal && (
        <Modal
          closeHandler={() => setShowTourRunIdDetailModal(undefined)}
          escPressClose
          header="Checkpoint Tour Detail"
        >
          <CheckpointTourRunDetails
            checkpointTourRunId={showTourRunIdDetailModal}
          />
        </Modal>
      )}
    </div>
  )
}

export {CheckpointTourRunsList}
